import React, { useEffect, useRef, useState } from 'react'
import { List, Map } from 'immutable'
import _get from 'lodash/get'
import {
  useSelector, useDispatch, connect as connectRedux, ReactReduxContext,
} from 'react-redux'
import compose from 'recompose/compose'
import { DETAIL_PAGE_STATIC_KEY } from '../../../services/detail/index'
import { getBoundActions } from '../../../actions/index'
import { TYPE_FEATURE, TYPE_PREVIEW } from '../../../services/video/index'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { getSessionStorage, SESSION_STORAGE_SKIP_PRE_ROLL } from '../../../services/local-preferences/index'
import { getUserLanguagePrimary } from '../../../services/user/index'
import { EN } from '../../../services/languages/constants'
import { getPrimary } from '../../../services/languages/index'
import { isSubtypeYogaFitnessMeditation, getPrimaryCourse } from '../../../services/content/index'
import { selectAppViewportWidth, selectAppViewportHeight } from '../../../services/app/selectors'
import { selectOracleGuideOnSideClosed, selectOracleGuideEnabled } from '../../../services/oracle/selectors'
import { setOracleGuideOnSideClosed } from '../../../services/oracle/actions'
import VideoPlayer from '../../VideoPlayer/index'
import VideoPlayerContainer from '../../VideoPlayerContainer/index'
import VideoPreRoll from '../../VideoPreRoll/index'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_BUTTON } from '../../ButtonSignUp/index'
import { AppDrawer } from '../../AppDrawer/index'
import { InlineGuideContent } from '../../OracleSideGuide/InlineGuideContent'
import { get as getConfig } from '../../../config/index'

const config = getConfig()
const preRollEnabled = _get(config, ['features', 'preRollPoC', 'enabled'])
const preRollMediaId = _get(config, ['features', 'preRollPoC', 'mediaId'])

const MAX_PRE_ROLL_COUNT = 2

const videoStates = {
  PRE_ROLL_PLAYER: 'pre-roll-player',
  VIDEO_PLAYER: 'video-player',
  GEO_NOT_ALLOWED: 'geo-not-allowed',
  NOT_ALLOWED: 'not-allowed',
  ERROR: 'error',
}

function shouldRenderPreRoll (props) {
  const {
    preRollPoC,
    user,
    auth,
    playerType,
    contentSubtype,
  } = props
  const language = getUserLanguagePrimary(user)
  const isYogaMeditationFitness = isSubtypeYogaFitnessMeditation({ contentSubtype })
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const userTenureDays = user.getIn(['data', 'userTenureDays'], 0)

  if (isLoggedIn && userTenureDays < 7) {
    return false
  }

  if (language !== EN || playerType !== TYPE_FEATURE || isYogaMeditationFitness) {
    return false
  }

  const skipPreRollPreference = getSessionStorage(SESSION_STORAGE_SKIP_PRE_ROLL, false)
  const mediaId = preRollPoC.get('mediaId', preRollMediaId)
  const count = preRollPoC.get('count', 0)
  return preRollEnabled
    && !skipPreRollPreference
    && (mediaId !== preRollMediaId || count < MAX_PRE_ROLL_COUNT)
}

function getInitialVideoState (props) {
  const {
    detailData,
    preRollPoC,
    user,
    auth,
    playerType,
  } = props

  if (playerType === TYPE_PREVIEW) {
    const previewMedia = detailData.get('previewMedia')
    return previewMedia ? videoStates.VIDEO_PLAYER : videoStates.ERROR
  }

  const featureMedia = detailData.get('featureMedia')

  if (!featureMedia) {
    return videoStates.ERROR
  }

  const contentSubtype = detailData.get('contentSubtype')
  const videoAllowed = featureMedia.getIn(['contentPolicy', 'accessCheck', 'allowed'])
  const geoAllowed = featureMedia.getIn(['contentPolicy', 'accessCheck', 'countryAllowed'])
  const renderPreRoll = shouldRenderPreRoll({
    preRollPoC,
    user,
    auth,
    playerType,
    contentSubtype,
  })

  if (!geoAllowed) {
    return videoStates.GEO_NOT_ALLOWED
  }

  if (!videoAllowed) {
    return videoStates.NOT_ALLOWED
  }

  return renderPreRoll ? videoStates.PRE_ROLL_PLAYER : videoStates.VIDEO_PLAYER
}

function DetailVideoPlayer (props) {
  const {
    detailData,
    user,
    auth,
    apolloClient,
    location,
    history,
    video,
    media,
    userVideo,
    getVideoData,
    getMediaData,
    getUserVideoData,
    resetVideo,
    resetMedia,
    resetUserVideo,
    preRollPoC,
    staticText,
  } = props

  const jwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const reduxContext = React.useContext(ReactReduxContext)
  const store = reduxContext ? reduxContext.store : null
  const path = _get(location, 'pathname')
  const playerType = _get(location, ['query', 'fullplayer']) || null
  const startAtRaw = _get(location, ['query', 'startAt'])
  const startAt = startAtRaw ? parseInt(startAtRaw, 10) || null : null
  const contentId = detailData.get('contentId')
  const mediaId = playerType === TYPE_FEATURE
    ? Number(detailData.getIn(['featureMedia', 'mediaId']))
    : Number(detailData.getIn(['previewMedia', 'mediaId']))
  const isVideoProcessing = video.get('processing')
  const isMediaProcessing = media.get('processing')
  const isUserVideoProcessing = userVideo.get('processing')
  const shouldLoadUserVideo = isLoggedIn && playerType === TYPE_FEATURE
  const userVideoReady = shouldLoadUserVideo ? (contentId === userVideo.get('id')) : true
  const isPlayerReady = (
    video.get('id') === contentId
    && media.get('id') === mediaId
    && userVideoReady
    && !isVideoProcessing
    && !isMediaProcessing
    && !isUserVideoProcessing
  )
  const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))
  const primaryCourse = getPrimaryCourse({ data: detailData, userLanguage })

  const [videoState, setVideoState] = useState(getInitialVideoState({
    detailData,
    preRollPoC,
    user,
    auth,
    playerType,
  }))
  const [isPlayerActive, setIsPlayerActive] = useState(false)

  // --- Inline AI Guide (mobile portrait) ---
  const dispatch = useDispatch()
  const viewportWidth = useSelector(selectAppViewportWidth)
  const viewportHeight = useSelector(selectAppViewportHeight)
  const isMobilePortrait = viewportWidth < 819 && viewportHeight > viewportWidth
  const guideEnabled = useSelector(selectOracleGuideEnabled)
  const isInlineGuideEnabled = !!config?.features?.ai?.oracleInlineGuide
    && isMobilePortrait && guideEnabled
  const guideClosed = useSelector(selectOracleGuideOnSideClosed)
  const [playerRef, setPlayerRef] = useState(null)
  const wrapperRef = useRef(null)

  // Compute IDs for InlineGuideContent
  const contentIdStr = contentId != null ? String(contentId) : null
  const rawFeatureMediaId = detailData.getIn(['featureMedia', 'mediaId'])
  const featureMediaIdStr = rawFeatureMediaId != null ? String(rawFeatureMediaId) : null

  // Auto-open the inline guide 1 second after the full video starts playing.
  // Must live here (not inside InlineGuideContent) because the AppDrawer
  // doesn't render children until isOpen becomes true.
  // Gates on playerType === TYPE_FEATURE (not preview) and videoState ===
  // VIDEO_PLAYER (not pre-roll) to avoid opening during previews or ads.
  const hasAutoOpenedRef = useRef(false)
  useEffect(() => {
    if (!isInlineGuideEnabled || !isPlayerActive || hasAutoOpenedRef.current
        || playerType !== TYPE_FEATURE
        || videoState !== videoStates.VIDEO_PLAYER) return undefined
    hasAutoOpenedRef.current = true
    const timerId = setTimeout(() => {
      dispatch(setOracleGuideOnSideClosed(false))
    }, 1000)
    return () => { clearTimeout(timerId) }
  }, [isInlineGuideEnabled, isPlayerActive, playerType, videoState, dispatch])

  // Dynamically compute --app-drawer-top from the actual video container
  // position so the drawer sits exactly below the video regardless of
  // header height, alert bars, safe-area insets, or scroll position.
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!isInlineGuideEnabled || !wrapper) return undefined
    const videoContainer = wrapper.querySelector('.detail-page__video-player-container')
    if (!videoContainer) return undefined

    const update = () => {
      const { bottom } = videoContainer.getBoundingClientRect()
      wrapper.style.setProperty('--app-drawer-top', `${bottom}px`)
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(videoContainer)

    // Also observe the header — banners/alert bars change its height,
    // which shifts the video down without changing the video's own size.
    const header = document.querySelector('header[role="banner"]')
    if (header) observer.observe(header)

    return () => observer.disconnect()
  }, [isInlineGuideEnabled])

  const handleSkipPreRoll = () => {
    setVideoState(videoStates.VIDEO_PLAYER)
  }

  const handlePreRollPlay = () => {
    setIsPlayerActive(true)
  }

  useEffect(() => {
    if (contentId && contentId !== video.get('id')
        && !isVideoProcessing
        && videoState === videoStates.VIDEO_PLAYER) {
      getVideoData({
        id: contentId,
        path,
        language: user.getIn(['data', 'language'], List()),
      })
    }
  }, [contentId, video, isVideoProcessing, getVideoData, videoState])

  useEffect(() => {
    if (mediaId && mediaId !== media.get('id')
        && !isMediaProcessing
        && videoState === videoStates.VIDEO_PLAYER) {
      getMediaData({
        id: mediaId,
        path,
        auth,
        user,
      })
    }
  }, [mediaId, media, isMediaProcessing, getMediaData, videoState])

  useEffect(() => {
    if (contentId && contentId !== userVideo.get('id')
        && shouldLoadUserVideo
        && !isUserVideoProcessing
        && videoState === videoStates.VIDEO_PLAYER) {
      getUserVideoData(contentId, path, jwt)
    }
  }, [
    contentId,
    userVideo,
    shouldLoadUserVideo,
    isUserVideoProcessing,
    getUserVideoData,
    jwt,
    videoState,
  ])

  useEffect(() => {
    if (media.size > 0 && !media.getIn(['data', 'bcHLS'], null) && !isMediaProcessing) {
      setVideoState(videoStates.ERROR)
    }
  }, [media, isMediaProcessing])

  useEffect(() => {
    return () => {
      resetVideo()
      resetMedia()
      resetUserVideo()
    }
  }, [])

  const wrapperClassName = isInlineGuideEnabled
    ? 'detail-page__video-player-wrapper detail-page__video-player-wrapper--inline-guide'
    : 'detail-page__video-player-wrapper'

  return (
   <div className={wrapperClassName} ref={wrapperRef}>
    <VideoPlayerContainer
      isPlayerActive={isPlayerActive}
      className="detail-page__video-player-container"
    >
      {videoState === videoStates.PRE_ROLL_PLAYER && (
        <VideoPreRoll
          mediaId={preRollMediaId}
          onPreRollComplete={handleSkipPreRoll}
          onPreRollPlay={handlePreRollPlay}
        />
      )}
      {videoState === videoStates.VIDEO_PLAYER && isPlayerReady && (
        <VideoPlayer
          store={store}
          location={location}
          history={history}
          apolloClient={apolloClient}
          onVideoPlay={() => setIsPlayerActive(true)}
          onVideoEnded={() => setIsPlayerActive(false)}
          onPlayerReady={isInlineGuideEnabled ? setPlayerRef : undefined}
          type={playerType}
          startAt={startAt}
          skipContainerStyles
          primaryCourse={primaryCourse}
        />
      )}
      {videoState === videoStates.GEO_NOT_ALLOWED && (
        <div className="detail-page__video-player-not-allowed">
          <p className="detail-page__video-player-not-allowed-text">{staticText.get('notAvailableInArea')}</p>
        </div>
      )}
      {videoState === videoStates.NOT_ALLOWED && (
        <div className="detail-page__video-player-not-allowed">
          <p className="detail-page__video-player-not-allowed-text">{staticText.get('noActiveSubscription')}</p>
          <ButtonSignUp
            text={staticText.get('signUpToWatch')}
            type={BUTTON_SIGN_UP_TYPE_BUTTON}
            scrollToTop
          />
        </div>
      )}
      {videoState === videoStates.ERROR && (
        <div className="detail-page__video-player-not-allowed">
          <p className="detail-page__video-player-not-allowed-text">{staticText.get('videoNotAvailable')}</p>
        </div>
      )}
    </VideoPlayerContainer>
    {isInlineGuideEnabled && (
      <AppDrawer
        mode="bottom-sheet"
        isOpen={!guideClosed}
        onClose={() => dispatch(setOracleGuideOnSideClosed(true))}
        draggable
        lockBodyScroll
      >
        <InlineGuideContent
          contentId={contentIdStr}
          featureMediaId={featureMediaIdStr}
          player={playerRef}
        />
      </AppDrawer>
    )}
   </div>
  )
}

export default compose(
  connectRedux(
    (state) => ({
      user: state.user,
      auth: state.auth,
      video: state.video,
      media: state.media,
      userVideo: state.userVideo,
      preRollPoC: state.featureTracking.getIn(['data', 'preRollPoC'], Map()) || Map(),
      staticText: state.staticText.getIn(['data', DETAIL_PAGE_STATIC_KEY, 'data']),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        getVideoData: actions.video.getVideoData,
        getMediaData: actions.media.getMediaData,
        getUserVideoData: actions.userVideo.getUserVideoData,
        resetVideo: actions.video.resetVideo,
        resetMedia: actions.media.resetMedia,
        resetUserVideo: actions.userVideo.resetUserVideo,
      }
    },
  ),
)(DetailVideoPlayer)
